import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  I18nManager,
  Modal,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { siteApi } from '../services/api';
import { getErrorMessage, parseError } from '../utils/errorHandler';
import { canManageSites } from '../utils/permissions';
import { useSites } from '../hooks/queries';
import { useCreateSite, useUpdateSite, useDeleteSite } from '../hooks/mutations';
import ErrorView from '../components/ErrorView';
import LoadingScreen from '../components/LoadingScreen';

interface Site {
  id: string;
  name: string;
  address?: string;
  createdAt: string;
}

export default function SitesManagementScreen({ navigation }: any) {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const {
    data: sites = [],
    isLoading,
    error: queryError,
    refetch,
    isRefetching,
  } = useSites(user?.orgId);
  const createSiteMutation = useCreateSite();
  const updateSiteMutation = useUpdateSite();
  const deleteSiteMutation = useDeleteSite();
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [saving, setSaving] = useState(false);

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;
  const hasPermission = user?.roles ? canManageSites(user.roles) : false;
  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'إدارة المواقع' : 'Sites Management',
      headerTintColor: '#ffffff',
      headerTitleStyle: {
        color: '#ffffff',
        fontWeight: '700',
      },
      headerRight: () => (
        <TouchableOpacity
          style={{ marginRight: 16 }}
          onPress={() => openModal()}
          accessible={true}
          accessibilityLabel={isArabic ? 'إضافة موقع جديد' : 'Add new site'}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لفتح نموذج إضافة موقع جديد'
              : 'Double tap to open form to add a new site'
          }
          accessibilityRole="button"
        >
          <Ionicons name="add-circle" size={28} color="#ffffff" />
        </TouchableOpacity>
      ),
    });

    if (!hasPermission) {
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية للوصول لهذه الصفحة'
          : 'You do not have permission to access this page.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      return;
    }
  }, [navigation, theme, isArabic, hasPermission, user?.orgId]);

  const openModal = (site?: Site) => {
    if (site) {
      setEditingSite(site);
      setSiteName(site.name);
      setSiteAddress(site.address || '');
    } else {
      setEditingSite(null);
      setSiteName('');
      setSiteAddress('');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSite(null);
    setSiteName('');
    setSiteAddress('');
  };

  const handleSave = async () => {
    if (!siteName.trim()) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء إدخال اسم الموقع' : 'Please enter site name',
      );
      return;
    }

    try {
      setSaving(true);

      if (editingSite) {
        await updateSiteMutation.mutateAsync({
          siteId: editingSite.id,
          data: { name: siteName.trim(), address: siteAddress.trim() || undefined },
        });
        Alert.alert(
          isArabic ? 'نجح' : 'Success',
          isArabic ? 'تم تحديث الموقع بنجاح' : 'Site updated successfully',
        );
      } else {
        await createSiteMutation.mutateAsync({
          name: siteName.trim(),
          address: siteAddress.trim() || undefined,
          orgId: user?.orgId,
        });
        Alert.alert(
          isArabic ? 'نجح' : 'Success',
          isArabic ? 'تم إضافة الموقع بنجاح' : 'Site added successfully',
        );
      }

      closeModal();
    } catch (error) {
      const appError = parseError(error);
      Alert.alert(isArabic ? 'خطأ' : 'Error', getErrorMessage(appError, isArabic ? 'ar' : 'en'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (site: Site) => {
    Alert.alert(
      isArabic ? 'تأكيد الحذف' : 'Confirm Delete',
      isArabic ? `هل تريد حذف الموقع "${site.name}"؟` : `Delete site "${site.name}"?`,
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'حذف' : 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteSiteMutation.mutateAsync(site.id);
              Alert.alert(
                isArabic ? 'نجح' : 'Success',
                isArabic ? 'تم حذف الموقع' : 'Site deleted',
              );
            } catch (error) {
              const appError = parseError(error);
              Alert.alert(
                isArabic ? 'خطأ' : 'Error',
                getErrorMessage(appError, isArabic ? 'ar' : 'en'),
              );
            }
          },
        },
      ],
    );
  };

  const renderSite = ({ item }: { item: Site }) => (
    <View
      style={[
        styles.siteCard,
        { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
      ]}
    >
      <View style={[styles.siteIcon, { backgroundColor: theme.colors.info + '15' }]}>
        <Ionicons name="location" size={24} color={theme.colors.info} />
      </View>

      <View style={[styles.siteInfo, isRTL && { alignItems: 'flex-end' }]}>
        <Text
          style={[
            styles.siteName,
            { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
          ]}
        >
          {item.name}
        </Text>
        {item.address && (
          <Text
            style={[
              styles.siteAddress,
              { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
            ]}
          >
            {item.address}
          </Text>
        )}
      </View>

      <View style={[styles.actions, false]}>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: theme.colors.primary + '15' }]}
          onPress={() => openModal(item)}
          accessible={true}
          accessibilityLabel={`${isArabic ? 'تعديل الموقع' : 'Edit site'}: ${item.name}`}
          accessibilityHint={
            isArabic ? 'انقر نقرًا مزدوجًا لتعديل تفاصيل الموقع' : 'Double tap to edit site details'
          }
          accessibilityRole="button"
        >
          <Ionicons name="create" size={18} color={theme.colors.primary} />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: theme.colors.error + '15' }]}
          onPress={() => handleDelete(item)}
          accessible={true}
          accessibilityLabel={`${isArabic ? 'حذف الموقع' : 'Delete site'}: ${item.name}`}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لحذف الموقع بشكل دائم. سيُطلب منك التأكيد'
              : 'Double tap to permanently delete site. Confirmation will be required'
          }
          accessibilityRole="button"
        >
          <Ionicons name="trash" size={18} color={theme.colors.error} />
        </TouchableOpacity>
      </View>
    </View>
  );

  const error = queryError ? (queryError as Error).message : null;

  if (isLoading && !isRefetching) {
    return <LoadingScreen message={isArabic ? 'جاري تحميل المواقع...' : 'Loading sites...'} />;
  }

  if (error && !isLoading && !isRefetching) {
    return (
      <ErrorView
        message={error}
        onRetry={() => refetch()}
        icon="location-outline"
        title={isArabic ? 'فشل تحميل المواقع' : 'Failed to Load Sites'}
      />
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <FlatList
        data={sites}
        renderItem={renderSite}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={() => refetch()} />}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Ionicons name="location-outline" size={64} color={theme.colors.textSecondary} />
            <Text style={[styles.emptyText, { color: theme.colors.text }]}>
              {isArabic ? 'لا توجد مواقع' : 'No Sites'}
            </Text>
            <Text style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'اضغط + لإضافة موقع جديد' : 'Tap + to add a new site'}
            </Text>
          </View>
        }
      />

      {/* Add/Edit Modal */}
      <Modal visible={showModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {editingSite
                  ? isArabic
                    ? 'تعديل الموقع'
                    : 'Edit Site'
                  : isArabic
                  ? 'إضافة موقع'
                  : 'Add Site'}
              </Text>
              <TouchableOpacity
                onPress={closeModal}
                accessible={true}
                accessibilityLabel={isArabic ? 'إغلاق' : 'Close'}
                accessibilityHint={
                  isArabic
                    ? 'انقر نقرًا مزدوجًا لإغلاق النموذج بدون حفظ'
                    : 'Double tap to close form without saving'
                }
                accessibilityRole="button"
              >
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                  textAlign: isRTL ? 'right' : 'left',
                },
              ]}
              placeholder={isArabic ? 'اسم الموقع *' : 'Site Name *'}
              placeholderTextColor={theme.colors.textSecondary}
              value={siteName}
              onChangeText={setSiteName}
              accessible={true}
              accessibilityLabel={isArabic ? 'اسم الموقع (مطلوب)' : 'Site name (required)'}
              accessibilityHint={
                isArabic ? 'أدخل اسمًا فريدًا للموقع' : 'Enter a unique name for the site'
              }
            />

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                  textAlign: isRTL ? 'right' : 'left',
                },
              ]}
              placeholder={isArabic ? 'العنوان (اختياري)' : 'Address (optional)'}
              placeholderTextColor={theme.colors.textSecondary}
              value={siteAddress}
              onChangeText={setSiteAddress}
              accessible={true}
              accessibilityLabel={isArabic ? 'عنوان الموقع (اختياري)' : 'Site address (optional)'}
              accessibilityHint={
                isArabic
                  ? 'أدخل عنوان الموقع الفعلي إن وجد'
                  : 'Enter physical address of the site if available'
              }
            />

            <TouchableOpacity
              style={[styles.saveButton, { backgroundColor: theme.colors.primary }]}
              onPress={handleSave}
              disabled={saving}
              accessible={true}
              accessibilityLabel={
                saving
                  ? isArabic
                    ? 'جاري الحفظ...'
                    : 'Saving...'
                  : editingSite
                  ? isArabic
                    ? 'تحديث الموقع'
                    : 'Update site'
                  : isArabic
                  ? 'إضافة موقع'
                  : 'Add site'
              }
              accessibilityHint={
                saving
                  ? isArabic
                    ? 'يرجى الانتظار أثناء حفظ الموقع'
                    : 'Please wait while site is being saved'
                  : editingSite
                  ? isArabic
                    ? 'انقر نقرًا مزدوجًا لحفظ التغييرات على الموقع'
                    : 'Double tap to save changes to site'
                  : isArabic
                  ? 'انقر نقرًا مزدوجًا لإنشاء موقع جديد'
                  : 'Double tap to create new site'
              }
              accessibilityRole="button"
              accessibilityState={{ disabled: saving, busy: saving }}
            >
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.saveButtonText}>
                  {editingSite ? (isArabic ? 'تحديث' : 'Update') : isArabic ? 'إضافة' : 'Add'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    padding: 16,
    paddingBottom: 100,
  },
  siteCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 14,
    marginBottom: 10,
    borderWidth: 1,
    gap: 12,
  },
  siteIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  siteInfo: {
    flex: 1,
    gap: 4,
  },
  siteName: {
    fontSize: 16,
    fontWeight: '600',
  },
  siteAddress: {
    fontSize: 13,
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 20,
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  input: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    fontSize: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  saveButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '700',
  },
});
